import React, { useEffect, useState } from 'react';
import { blackout } from '../services/api';
import { PlayCircle, ExternalLink } from 'lucide-react';

/**
 * Public intro video banner shown at the top of /blackout and /blackout/info.
 * Admin-managed via /admin/blackout/intro-video. Renders nothing when disabled
 * or unset — so the Blackout Game dashboard looks unchanged until an admin
 * publishes a video.
 */

// Extract a YouTube video ID from any form of URL we accept — handles
// /watch?v=, youtu.be/, /shorts/, /embed/, /live/.
function extractYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/|live\/))([A-Za-z0-9_\-]{6,20})/);
  return m ? m[1] : null;
}

export default function BlackoutIntroVideo() {
  const [v, setV] = useState(null);
  const [showIframe, setShowIframe] = useState(false);

  useEffect(() => {
    blackout.introVideo().then(setV).catch(() => setV(null));
  }, []);

  if (!v || !v.enabled || !v.url) return null;

  const isYouTubeOrVimeo = v.type === 'embed';
  const src = v.embedUrl || v.url;
  const ytId = isYouTubeOrVimeo ? extractYouTubeId(v.url || v.embedUrl) : null;
  // Click-to-play thumbnail prevents YouTube "Video unavailable" errors from
  // showing on page load if the uploader hasn't enabled embedding — users
  // always see a clean preview and can either play inline or jump to YouTube.
  const posterUrl = ytId ? `https://i.ytimg.com/vi/${ytId}/hqdefault.jpg` : null;
  const watchOnYouTubeUrl = ytId ? `https://www.youtube.com/watch?v=${ytId}` : null;

  return (
    <section className="relative overflow-hidden rounded-3xl border border-emerald-500/30 bg-gradient-to-br from-emerald-950/40 via-black to-neutral-950"
             data-testid="blackout-intro-video">
      <div className="absolute -top-20 -right-10 w-72 h-72 bg-emerald-500/15 blur-3xl rounded-full pointer-events-none"/>
      <div className="relative p-5 md:p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[10px] font-black uppercase tracking-wider">
              <PlayCircle size={12}/> Watch first
            </div>
            {v.title && <h2 className="mt-2 text-xl md:text-2xl font-black text-white">{v.title}</h2>}
            {v.description && (
              <p className="mt-1 text-sm text-neutral-300 max-w-2xl leading-relaxed">
                {v.description}
              </p>
            )}
          </div>
        </div>

        <div className="relative w-full rounded-xl overflow-hidden bg-black border border-neutral-800" style={{ aspectRatio: '16 / 9' }}>
          {!isYouTubeOrVimeo ? (
            // Direct video file — native <video> player.
            <video
              src={src}
              controls
              playsInline
              preload="metadata"
              className="absolute inset-0 w-full h-full object-contain bg-black"
              data-testid="blackout-intro-video-player"
            />
          ) : ytId && !showIframe ? (
            // YouTube — click-to-play poster: skips the "Video unavailable" splash
            // for videos whose uploader hasn't enabled embedding on YouTube Studio.
            <button type="button"
              onClick={() => setShowIframe(true)}
              className="absolute inset-0 w-full h-full group"
              data-testid="blackout-intro-video-poster"
              aria-label="Play video">
              <img
                src={posterUrl}
                alt={v.title || 'Intro video'}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <span className="absolute inset-0 bg-black/35 group-hover:bg-black/25 transition-colors"/>
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="w-20 h-20 rounded-full bg-red-600 group-hover:bg-red-500 flex items-center justify-center shadow-2xl">
                  <PlayCircle size={44} className="text-white -mr-1"/>
                </span>
              </span>
            </button>
          ) : (
            <iframe
              src={src + (src.includes('?') ? '&' : '?') + 'autoplay=1&playsinline=1&rel=0'}
              title={v.title || 'Blackout Game intro video'}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
              data-testid="blackout-intro-video-iframe"
            />
          )}
        </div>

        {watchOnYouTubeUrl && (
          <div className="mt-3 flex items-center justify-end">
            <a href={watchOnYouTubeUrl} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1.5 text-xs text-neutral-300 hover:text-emerald-300 underline-offset-4 hover:underline"
               data-testid="blackout-intro-video-watch-on-youtube">
              Having trouble playing? Watch on YouTube <ExternalLink size={12}/>
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
